/* eslint-disable import/no-named-as-default */
import { useState } from 'react'
import { Transition } from '@headlessui/react'

import { MEMBERSHIP_PRICE } from 'config'
import { MEMBERSHIP_BENEFITS_LIST } from 'constants/membership'
import MembershipCardContent from './MembershipCardContent'
import FadeIn, { delays, FadeInOut } from './transitions/FadeIn'
import TextButton from './textButton'
import PrimaryButton from './primaryButton'

const Title = ({ section }: { section: string | JSX.Element }) => (
  <div className='text-center md:text-2xl'>
    Suscribite a{' '}
    <span className='font-semibold'>
      Wayni<span className='text-green-800'>+</span>
    </span>
    <div>para habilitar {section}</div>
  </div>
)

interface MembershipCardProps {
  primarybuttonFunc: () => void
  textButtonFunc?: () => void
  isLoading?: boolean
  twoCols?: boolean
  titleSection?: string | JSX.Element
}

export const MembershipCard = ({
  primarybuttonFunc,
  textButtonFunc = null,
  isLoading = false,
  twoCols = true,
  titleSection = 'tu tarjeta física'
}: MembershipCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const handleButton = () => setIsExpanded((prevState) => !prevState)

  return (
    <>
      <MembershipCardContent
        twoCols={twoCols}
        title={<Title section={titleSection} />}
        badge={`Por solo $ ${MEMBERSHIP_PRICE}`}
        dataTestId='MembershipCardContent'
      >
        <div className='w-full flex items-center justify-center flex-col'>
          <ul className='mt-2 '>
            <div>
              {MEMBERSHIP_BENEFITS_LIST.slice(0, 4).map((item, idx) => (
                <li
                  key={idx}
                  className='my-3 w-full flex items-start justify-start list-none'
                >
                  <img
                    className='shrink-0 w-3 mt-[5px]'
                    src={item.img}
                    alt={item.alt}
                  />
                  <div className='w-full ml-2'>
                    <h6 className='text-black font-bold text-xs'>
                      {item.title}
                    </h6>
                    <p className='text-black font-normal text-2xs'>
                      {item.description}
                    </p>
                    {item.clarification && (
                      <p className='text-[#7C7C7C] text-2xs'>
                        {item.clarification}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </div>
            <div className='w-full flex items-center justify-center md:justify-end'>
              <Transition.Root show={isExpanded}>
                {isExpanded &&
                  MEMBERSHIP_BENEFITS_LIST.slice(4, 8).map((item, idx) => (
                    <FadeIn key={idx} delay={delays[idx]}>
                      <li
                        key={idx}
                        className='my-1 w-full flex items-start justify-start list-none'
                      >
                        <img
                          className='shrink-0 w-3 mt-[5px]'
                          src={item.img}
                          alt={item.alt}
                        />
                        <div className='w-full ml-2'>
                          <h6 className='text-black font-bold text-xs'>
                            {item.title}
                          </h6>
                          <p className='text-black font-normal text-2xs'>
                            {item.description}
                          </p>
                          {item.clarification && (
                            <p className='text-[#7C7C7C] text-2xs'>
                              {item.clarification}
                            </p>
                          )}
                        </div>
                      </li>
                    </FadeIn>
                  ))}
              </Transition.Root>
            </div>
            <div className='w-full mt-1 text-center flex justify-center'>
              <TextButton
                handleFunction={textButtonFunc || handleButton}
                className='font-extrabold text-2xs text-blue-900 mt-3'
              >
                {isExpanded ? 'Mostrar menos' : 'Ver más beneficios'}
              </TextButton>
            </div>
          </ul>
        </div>
      </MembershipCardContent>
      <PrimaryButton
        className='mt-6 w-full md:mt-10 mb-5 md:mb-0'
        buttonText='Suscribirme'
        data-testid='subscriptionButton'
        isLoading={isLoading}
        primaryButtonFunction={() => {
          primarybuttonFunc()
        }}
      />
    </>
  )
}

export const AdvanceContent = ({ isExpanded }: { isExpanded: boolean }) => (
  <div className='w-full'>
    <Transition.Root as='div' show={isExpanded}>
      {MEMBERSHIP_BENEFITS_LIST.map((item, idx) => (
        <FadeInOut
          key={idx}
          enterDelay={delays[idx]}
          exitDelay={delays[MEMBERSHIP_BENEFITS_LIST.length - (idx + 1)]}
        >
          <li key={idx} className='my-3 list-none'>
            <h6 className='text-black font-bold text-xs'>{item.title}</h6>
            <p className='text-black font-normal text-2xs'>
              {item.description}
            </p>
            {item.clarification && (
              <p className='text-[#7C7C7C] text-2xs'>{item.clarification}</p>
            )}
          </li>
        </FadeInOut>
      ))}
    </Transition.Root>
  </div>
)
